// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Layout from './components/Layout';
import LegalWatermark from './components/LegalWatermark';
import './App.css';

// ── Lazy-loaded pages (code splitting) ──
// Auth pages (small, load fast)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PasswordChangePage = lazy(() => import('./pages/PasswordChangePage'));
const NDASignaturePage = lazy(() => import('./pages/NDASignaturePage'));
const AppTutorialPage = lazy(() => import('./pages/AppTutorialPage'));
// Core pages
const OverwatchPage = lazy(() => import('./pages/OverwatchPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const JobDetailPage = lazy(() => import('./pages/JobDetailPage'));
const CreateJobPage = lazy(() => import('./pages/CreateJobPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const QuotesPage = lazy(() => import('./pages/QuotesPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ReceptionDashboard = lazy(() => import('./pages/ReceptionDashboard'));
const TestReportsPage = lazy(() => import('./pages/TestReportsPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const RoleViewPage = lazy(() => import('./pages/RoleViewPage'));
const FounderDashboard = lazy(() => import('./pages/FounderDashboard'));
const SOPLibraryPage = lazy(() => import('./pages/SOPLibraryPage'));
const SiteRecordingsPage = lazy(() => import('./pages/SiteRecordingsPage'));
const ESignatureConsentPage = lazy(() => import('./pages/ESignatureConsentPage'));
const TranscriptionsPage = lazy(() => import('./pages/TranscriptionsPage'));
const DeliveryPermitsPage = lazy(() => import('./pages/DeliveryPermitsPage'));
const VerticalSelectionPage = lazy(() => import('./pages/VerticalSelectionPage'));
// Electrical
const ElectricalDashboard = lazy(() => import('./pages/ElectricalDashboard'));
const ElectricalSOPPage = lazy(() => import('./pages/ElectricalSOPPage'));
const PanelSchedulePage = lazy(() => import('./pages/PanelSchedulePage'));
const PermitsPage = lazy(() => import('./pages/PermitsPage'));
const ElectricalCodePage = lazy(() => import('./pages/ElectricalCodePage'));
const LoadCalcPage = lazy(() => import('./pages/LoadCalcPage'));
// HVAC
const HVACDashboard = lazy(() => import('./pages/hvac/HVACDashboard'));
const HVACSOPPage = lazy(() => import('./pages/hvac/HVACSOPPage'));
const HVACCodePage = lazy(() => import('./pages/hvac/HVACCodePage'));
const HVACCalcPage = lazy(() => import('./pages/hvac/HVACCalcPage'));
const HVACPermitsPage = lazy(() => import('./pages/hvac/HVACPermitsPage'));
// Plumbing
const PlumbingDashboard = lazy(() => import('./pages/plumbing/PlumbingDashboard'));
const PlumbingSOPPage = lazy(() => import('./pages/plumbing/PlumbingSOPPage'));
const PlumbingCodePage = lazy(() => import('./pages/plumbing/PlumbingCodePage'));
const PlumbingCalcPage = lazy(() => import('./pages/plumbing/PlumbingCalcPage'));
const PlumbingPermitsPage = lazy(() => import('./pages/plumbing/PlumbingPermitsPage'));
// Concrete
const ConcreteDashboard = lazy(() => import('./pages/concrete/ConcreteDashboard'));
const ConcreteSOPPage = lazy(() => import('./pages/concrete/ConcreteSOPPage'));
const ConcreteCodePage = lazy(() => import('./pages/concrete/ConcreteCodePage'));
const ConcreteCalcPage = lazy(() => import('./pages/concrete/ConcreteCalcPage'));
const ConcretePermitsPage = lazy(() => import('./pages/concrete/ConcretePermitsPage'));
// Construction
const ConstructionDashboard = lazy(() => import('./pages/construction/ConstructionDashboard'));
const ConstructionSOPPage = lazy(() => import('./pages/construction/ConstructionSOPPage'));
const ConstructionCodePage = lazy(() => import('./pages/construction/ConstructionCodePage'));
const ConstructionCalcPage = lazy(() => import('./pages/construction/ConstructionCalcPage'));
const ConstructionPermitsPage = lazy(() => import('./pages/construction/ConstructionPermitsPage'));
// Overhead Doors
const OverheadDoorsDashboard = lazy(() => import('./pages/overheaddoors/OverheadDoorsDashboard'));
const OverheadDoorsSOPPage = lazy(() => import('./pages/overheaddoors/OverheadDoorsSOPPage'));
const OverheadDoorsCodePage = lazy(() => import('./pages/overheaddoors/OverheadDoorsCodePage'));
const OverheadDoorsCalcPage = lazy(() => import('./pages/overheaddoors/OverheadDoorsCalcPage'));
const OverheadDoorsPermitsPage = lazy(() => import('./pages/overheaddoors/OverheadDoorsPermitsPage'));
const SpringEngineeringPage = lazy(() => import('./pages/SpringEngineeringPage'));
const SteelCraftProductsPage = lazy(() => import('./pages/SteelCraftProductsPage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const DocumentScannerPage = lazy(() => import('./pages/DocumentScannerPage'));
const CalendarHistoryPage = lazy(() => import('./pages/CalendarHistoryPage'));
const GmailCallbackPage = lazy(() => import('./pages/GmailCallbackPage'));
const EmailSetupPage = lazy(() => import('./pages/EmailSetupPage'));
const EntrapmentWaiverPage = lazy(() => import('./pages/EntrapmentWaiverPage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'));
const ITAdminPage = lazy(() => import('./pages/ITAdminPage'));
const TimeClockPage = lazy(() => import('./pages/TimeClockPage'));

// Loading spinner for lazy-loaded pages
function PageLoader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', color: '#1B3A5C',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 36, height: 36, border: '3px solid #e2e8f0',
          borderTopColor: '#1B3A5C', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          margin: '0 auto 12px',
        }} />
        <div style={{ fontSize: 14, fontWeight: 500 }}>Loading...</div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

const FOUNDER_EMAILS = [
  'harold@islandoverheaddoors.com', 'haroldblahut@gmail.com',
  'j.lutz@miskulintechnologies.com', 'jordan.l.lutz@gmail.com',
  'rmiskulin@gmail.com', 'r.miskulin@miskulintechnologies.com',
];

function FounderRoute({ children }) {
  const { user } = useAuth();
  const email = (user?.email || '').toLowerCase();
  if (!FOUNDER_EMAILS.includes(email)) return <Navigate to="/" />;
  return children;
}

function OnboardingGate({ children }) {
  const { user, onboardingStep, completeOnboardingStep } = useAuth();
  if (!user) return children;
  if (onboardingStep === 'password') return <PasswordChangePage onComplete={() => completeOnboardingStep('password')} />;
  if (onboardingStep === 'nda') return <NDASignaturePage onComplete={() => completeOnboardingStep('nda')} />;
  if (onboardingStep === 'tutorial') return <AppTutorialPage onComplete={() => completeOnboardingStep('tutorial')} />;
  // email_setup removed from onboarding gate — Jordan handles via Founder Dashboard
  return children;
}

function RoleHomePage() {
  const { user } = useAuth();
  // IOD is a dedicated overhead doors app — no vertical selection needed.
  // Auto-set the vertical to overhead doors on every login.
  try {
    localStorage.setItem('islandoverheaddoors_verticals', JSON.stringify(['overheaddoors']));
  } catch { /* ignore */ }
  // Harold = founder — goes straight to his dashboard
  if ((user?.email || '').toLowerCase() === 'harold@islandoverheaddoors.com') return <Navigate to="/founder" />;
  if ((user?.email || '').toLowerCase() === 'haroldblahut@gmail.com') return <Navigate to="/founder" />;
  // Suzanne is stored as 'dispatcher' but is actually reception
  if ((user?.email || '').toLowerCase() === 'info@islandoverheaddoors.com') return <Navigate to="/reception" />;
  // Clorice is stored as 'manager' but is actually accounting
  if ((user?.email || '').toLowerCase() === 'accounting@islandoverheaddoors.com') return <Navigate to="/invoices" />;
  const role = user?.role;
  if (role === 'technician') return <Navigate to="/jobs" />;
  if (role === 'accounting') return <Navigate to="/invoices" />;
  if (role === 'reception') return <Navigate to="/reception" />;
  // Owner/dispatch/office → Overwatch command center (spec Screen 8)
  return <Navigate to="/overwatch" />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/gmail-callback" element={<ProtectedRoute><GmailCallbackPage /></ProtectedRoute>} />
      <Route path="/setup/verticals" element={<ProtectedRoute><VerticalSelectionPage /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><OnboardingGate><Layout /></OnboardingGate></ProtectedRoute>}>
        <Route index element={<RoleHomePage />} />
        <Route path="overwatch" element={<OverwatchPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="reception" element={<ReceptionDashboard />} />
        <Route path="founder" element={<FounderRoute><FounderDashboard /></FounderRoute>} />
        <Route path="test-reports" element={<TestReportsPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="view/:role" element={<RoleViewPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="jobs/:jobId" element={<JobDetailPage />} />
        <Route path="jobs/new" element={<CreateJobPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="sops" element={<SOPLibraryPage />} />
        {/* Recording */}
        <Route path="site-recordings" element={<SiteRecordingsPage />} />
        <Route path="e-signature-consent" element={<ESignatureConsentPage />} />
        <Route path="transcriptions" element={<TranscriptionsPage />} />
        {/* Deliveries */}
        <Route path="delivery-permits" element={<DeliveryPermitsPage />} />
        {/* Electrical */}
        <Route path="electrical" element={<ElectricalDashboard />} />
        <Route path="electrical/sops" element={<ElectricalSOPPage />} />
        <Route path="electrical/panels" element={<PanelSchedulePage />} />
        <Route path="electrical/permits" element={<PermitsPage />} />
        <Route path="electrical/code" element={<ElectricalCodePage />} />
        <Route path="electrical/loadcalc" element={<LoadCalcPage />} />
        {/* HVAC */}
        <Route path="hvac" element={<HVACDashboard />} />
        <Route path="hvac/sops" element={<HVACSOPPage />} />
        <Route path="hvac/code" element={<HVACCodePage />} />
        <Route path="hvac/calculator" element={<HVACCalcPage />} />
        <Route path="hvac/permits" element={<HVACPermitsPage />} />
        {/* Plumbing */}
        <Route path="plumbing" element={<PlumbingDashboard />} />
        <Route path="plumbing/sops" element={<PlumbingSOPPage />} />
        <Route path="plumbing/code" element={<PlumbingCodePage />} />
        <Route path="plumbing/calculator" element={<PlumbingCalcPage />} />
        <Route path="plumbing/permits" element={<PlumbingPermitsPage />} />
        {/* Concrete */}
        <Route path="concrete" element={<ConcreteDashboard />} />
        <Route path="concrete/sops" element={<ConcreteSOPPage />} />
        <Route path="concrete/code" element={<ConcreteCodePage />} />
        <Route path="concrete/calculator" element={<ConcreteCalcPage />} />
        <Route path="concrete/permits" element={<ConcretePermitsPage />} />
        {/* Construction */}
        <Route path="construction" element={<ConstructionDashboard />} />
        <Route path="construction/sops" element={<ConstructionSOPPage />} />
        <Route path="construction/code" element={<ConstructionCodePage />} />
        <Route path="construction/calculator" element={<ConstructionCalcPage />} />
        <Route path="construction/permits" element={<ConstructionPermitsPage />} />
        {/* Overhead Doors */}
        <Route path="overheaddoors" element={<OverheadDoorsDashboard />} />
        <Route path="overheaddoors/sops" element={<OverheadDoorsSOPPage />} />
        <Route path="overheaddoors/code" element={<OverheadDoorsCodePage />} />
        <Route path="overheaddoors/calculator" element={<OverheadDoorsCalcPage />} />
        <Route path="overheaddoors/permits" element={<OverheadDoorsPermitsPage />} />
        <Route path="entrapment-waiver" element={<EntrapmentWaiverPage />} />
        <Route path="overheaddoors/products" element={<SteelCraftProductsPage />} />
        <Route path="spring-engineering" element={<SpringEngineeringPage />} />
        {/* Vendors */}
        <Route path="vendors" element={<VendorsPage />} />
        {/* Document Scanner */}
        <Route path="scanner" element={<DocumentScannerPage />} />
        {/* Schedule */}
        <Route path="schedule" element={<SchedulePage />} />
        {/* Calendar History */}
        <Route path="calendar-history" element={<CalendarHistoryPage />} />
        {/* AI Assistant */}
        <Route path="assistant" element={<AIAssistantPage />} />
        {/* IT Admin */}
        <Route path="it-admin" element={<ITAdminPage />} />
        {/* Time Clock */}
        <Route path="time-clock" element={<TimeClockPage />} />
        {/* email-setup removed from nav — Jordan handles via Founder Dashboard */}
        <Route path="profile" element={<ProfilePage />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LegalWatermark appName="IslandOverheadDoors" />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
